import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'
import { toSiteWallClockDate } from 'common/services/dateService'

// The EMR statuses the schedule endpoints send, plus 'unavailable' for time nobody covers.
export type AvailabilityStatus =
  | 'in_office'
  | 'out_of_office'
  | 'vacation'
  | 'not_available'
  | 'lunch'
  | 'reserved'
  | 'unavailable'

// Every status that takes the person away; in priority order for a single person's cell.
const AWAY_STATUSES = ['out_of_office', 'vacation', 'not_available', 'lunch', 'reserved'] as const

export type AvailabilityBlock = {
  status: AvailabilityStatus
  start: number
  end: number
  // The block's note from the EMR, shown after the status label.
  comment?: string
}

const DAY_SECONDS = 24 * 60 * 60

const STATUS_CLASS: Record<AvailabilityStatus, string> = {
  in_office: 'schedule-availability-in',
  out_of_office: 'schedule-availability-out',
  vacation: 'schedule-availability-vacation',
  not_available: 'schedule-availability-na',
  lunch: 'schedule-availability-lunch',
  reserved: 'schedule-availability-reserved',
  unavailable: 'schedule-availability-none',
}

const STATUS_LABEL: Partial<Record<AvailabilityStatus, string>> = {
  out_of_office: 'OUT',
  vacation: 'VACATION',
  not_available: 'N/A',
  lunch: 'LUNCH',
  reserved: 'RESERVED',
}

const toSeconds = (value: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = value.split(':').map((part) => Number(part))
  return hours * 3600 + minutes * 60 + seconds
}

export const dateToDaySeconds = (date: Date): number =>
  date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds()

const rangesOverlap = (start: number, end: number, itemStart: number, itemEnd: number): boolean =>
  start < itemEnd && end > itemStart

const itemsFor = (groups: DoctorAvailabilityGroup[], status: string, userId: number) =>
  groups
    .find((group) => group.status === status)
    ?.items.filter((item) => item.user_id === userId) ?? []

export const userHasAvailability = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
): boolean => !!groups?.some((group) => group.items.some((item) => item.user_id === userId))

export const getUserStatusAt = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
  seconds: number,
): AvailabilityStatus | null => {
  if (!groups?.length || !userId) return null

  const covers = (status: string) =>
    itemsFor(groups, status, userId).some(
      (item) => seconds >= toSeconds(item.start) && seconds < toSeconds(item.end),
    )

  const away = AWAY_STATUSES.find((status) => covers(status))
  if (away) return away
  if (covers('in_office')) return 'in_office'
  return null
}

/** In office wins when anyone selected is in. Reserved, lunch, and the away statuses show when nobody selected is in. */
export const getDisplayStatusAt = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
  seconds: number,
): AvailabilityStatus | null => {
  const statuses = userIds
    .map((userId) => getUserStatusAt(groups, userId, seconds))
    .filter((status): status is AvailabilityStatus => !!status)

  if (!statuses.length) return null
  if (statuses.some((status) => status === 'in_office')) return 'in_office'
  if (statuses.some((status) => status === 'lunch')) return 'lunch'
  if (statuses.some((status) => status === 'reserved')) return 'reserved'
  if (statuses.some((status) => status === 'out_of_office')) return 'out_of_office'
  if (statuses.some((status) => status === 'vacation')) return 'vacation'
  if (statuses.some((status) => status === 'not_available')) return 'not_available'
  return null
}

const COMMENT_JOINER = ' · '

// Every distinct note on items of that status covering this second, across the selected users.
const getCommentAt = (
  groups: DoctorAvailabilityGroup[],
  userIds: number[],
  status: AvailabilityStatus,
  seconds: number,
): string | undefined => {
  const items = groups.find((group) => group.status === status)?.items ?? []
  const notes = items
    .filter(
      (item) =>
        userIds.includes(item.user_id) &&
        seconds >= toSeconds(item.start) &&
        seconds < toSeconds(item.end),
    )
    .map((item) => item.comment?.trim() ?? '')
    .filter(Boolean)
  const distinct = [...new Set(notes)]
  return distinct.length ? distinct.join(COMMENT_JOINER) : undefined
}

const mergeBlocks = (blocks: AvailabilityBlock[]): AvailabilityBlock[] =>
  blocks.reduce<AvailabilityBlock[]>((merged, block) => {
    const previous = merged[merged.length - 1]
    const joins =
      previous &&
      previous.status === block.status &&
      previous.comment === block.comment &&
      previous.end === block.start
    if (joins) return [...merged.slice(0, -1), { ...previous, end: block.end }]
    return [...merged, { ...block }]
  }, [])

export const buildDisplayBlocks = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
): AvailabilityBlock[] => {
  if (!userIds.length) return []
  if (userIds.length === 1 && !userHasAvailability(groups, userIds[0])) {
    return [{ status: 'unavailable', start: 0, end: DAY_SECONDS }]
  }
  if (!groups?.length) return []

  const boundaries = new Set<number>([0, DAY_SECONDS])
  for (const group of groups) {
    for (const item of group.items) {
      if (!userIds.includes(item.user_id)) continue
      boundaries.add(toSeconds(item.start))
      boundaries.add(toSeconds(item.end))
    }
  }

  const points = [...boundaries].sort((left, right) => left - right)
  const raw: AvailabilityBlock[] = []
  for (let index = 0; index < points.length - 1; index++) {
    const status = getDisplayStatusAt(groups, userIds, points[index]) ?? 'unavailable'
    const comment = getCommentAt(groups, userIds, status, points[index])
    raw.push({ status, start: points[index], end: points[index + 1], ...(comment && { comment }) })
  }
  return mergeBlocks(raw)
}

const normalizeRange = (
  start: Date,
  end: Date,
  timeZone?: string,
): { start: number; end: number } | null => {
  const toSecondsOfDay = (date: Date) => {
    const wall = timeZone ? toSiteWallClockDate(date, timeZone) : date
    return dateToDaySeconds(wall ?? date)
  }
  const startSeconds = toSecondsOfDay(start)
  let endSeconds = toSecondsOfDay(end)
  if (endSeconds <= startSeconds) endSeconds = DAY_SECONDS
  if (endSeconds <= startSeconds) return null
  return { start: startSeconds, end: endSeconds }
}

export const isUserRangeUnavailable = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userId: number,
  start: Date,
  end: Date,
): boolean => {
  if (!userHasAvailability(groups, userId)) return false
  const range = normalizeRange(start, end)
  if (!range || !groups) return false

  return AWAY_STATUSES.some((status) =>
    itemsFor(groups, status, userId).some((item) =>
      rangesOverlap(range.start, range.end, toSeconds(item.start), toSeconds(item.end)),
    ),
  )
}

export const isSelectionRangeUnavailable = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
  start: Date,
  end: Date,
  timeZone?: string,
): boolean => {
  if (!userIds.length) return false
  const range = normalizeRange(start, end, timeZone)
  if (!range) return false

  return buildDisplayBlocks(groups, userIds).some(
    (block) =>
      block.status !== 'in_office' && rangesOverlap(range.start, range.end, block.start, block.end),
  )
}

// Each doctor judged alone: one in office never covers another who is away.
export const unavailableUserIds = (
  groups: DoctorAvailabilityGroup[] | undefined,
  userIds: number[],
  start: Date,
  end: Date,
  timeZone?: string,
): number[] =>
  userIds.filter((userId) => isSelectionRangeUnavailable(groups, [userId], start, end, timeZone))

export const availabilityClassName = (status: AvailabilityStatus): string => STATUS_CLASS[status]

// The block that starts inside this cell, if it has anything to say (a label or a comment).
// On the grid's first row a block that began earlier counts too, so it still gets its label.
const blockStartingInCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
  firstRow = false,
): AvailabilityBlock | null => {
  const start = dateToDaySeconds(cellStart)
  const end = start + slotMinutes * 60
  const startsHere = (item: AvailabilityBlock) =>
    firstRow ? item.start < end && item.end > start : item.start >= start && item.start < end
  return (
    blocks.find(
      (item) =>
        (STATUS_LABEL[item.status] || item.comment) &&
        item.status !== 'unavailable' &&
        startsHere(item),
    ) ?? null
  )
}

// A cell is one slot tall, so a long note is cut for the grid; the list shows it whole.
const CELL_COMMENT_MAX = 120

const shortenForCell = (comment: string): string => {
  const chars = Array.from(comment)
  if (chars.length <= CELL_COMMENT_MAX) return comment
  return `${chars
    .slice(0, CELL_COMMENT_MAX - 1)
    .join('')
    .trimEnd()}…`
}

export const availabilityLabelForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
  firstRow = false,
): string | null => {
  const block = blockStartingInCell(blocks, cellStart, slotMinutes, firstRow)
  return block ? STATUS_LABEL[block.status] ?? null : null
}

export const availabilityCommentForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
  firstRow = false,
): string | null => {
  const comment = blockStartingInCell(blocks, cellStart, slotMinutes, firstRow)?.comment
  return comment ? shortenForCell(comment) : null
}

// The full note of the block covering this cell, carried on every cell of it for the hover tooltip.
export const availabilityNoteForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
): string | null => {
  const seconds = dateToDaySeconds(cellStart)
  return blocks.find((block) => seconds >= block.start && seconds < block.end)?.comment ?? null
}

export const statusForCell = (
  blocks: AvailabilityBlock[],
  cellStart: Date,
): AvailabilityStatus | null => {
  const seconds = dateToDaySeconds(cellStart)
  return blocks.find((block) => seconds >= block.start && seconds < block.end)?.status ?? null
}

const STATUS_CLASSES = Object.values(STATUS_CLASS)

// Paints one Syncfusion work cell with the doctor's status; used by the Find Availability preview.
export const paintAvailabilityCell = (
  element: HTMLElement,
  blocks: AvailabilityBlock[],
  cellStart: Date,
  slotMinutes: number,
): void => {
  element.classList.remove(...STATUS_CLASSES)
  delete element.dataset.availabilityLabel
  delete element.dataset.availabilityComment
  delete element.dataset.availabilityNote
  if (element.classList.contains('e-disabled-cell')) return

  const status = statusForCell(blocks, cellStart)
  if (!status) return

  element.classList.add(availabilityClassName(status))
  // The grid may open after a block began (a 7:00 block on an 8:00 grid); its label moves to row one.
  const row = element.parentElement
  const firstRow = row instanceof HTMLTableRowElement && row.previousElementSibling == null
  const label = availabilityLabelForCell(blocks, cellStart, slotMinutes, firstRow)
  if (label) element.dataset.availabilityLabel = label
  const comment = availabilityCommentForCell(blocks, cellStart, slotMinutes, firstRow)
  if (comment) element.dataset.availabilityComment = comment
  const note = availabilityNoteForCell(blocks, cellStart)
  if (note) element.dataset.availabilityNote = note
}
